import { useMemo } from "react"
import { TriCheckbox, TriCheckboxState } from "@nova/component-checkbox"
import { Select } from "@nova/component-select"
import {
    NotificationCategory,
    NotificationChannel,
    NotificationPreferenceEntry,
    SilenceDuration,
    isSilenceActive,
    notificationCategoryMeta,
    silenceUntilToDuration,
} from "@nova/schemas"
import { NotificationSettingsDraft } from "./slice"

enum NotificationSection {
    Notifications = "Notifications",
    Processing = "Processing",
    LiveStream = "Live Stream",
    LocationSharing = "Location Sharing",
}

const categoryDisplay: Record<
    NotificationCategory,
    { section: NotificationSection; label: string }
> = {
    [NotificationCategory.AccessRequests]: {
        section: NotificationSection.Notifications,
        label: "Access Requests",
    },
    [NotificationCategory.ProcessingOrtho]: {
        section: NotificationSection.Processing,
        label: "Orthomosaic",
    },
    [NotificationCategory.ProcessingHotspot]: {
        section: NotificationSection.Processing,
        label: "Hotspot",
    },
    [NotificationCategory.ProcessingColorfinder]: {
        section: NotificationSection.Processing,
        label: "Colorfinder",
    },
    [NotificationCategory.ProcessingSatellite]: {
        section: NotificationSection.Processing,
        label: "Satellite",
    },
    [NotificationCategory.Comments]: {
        section: NotificationSection.Notifications,
        label: "Comments, replies and mentions",
    },
    [NotificationCategory.StreamStarted]: {
        section: NotificationSection.LiveStream,
        label: "When any workspace stream starts",
    },
    [NotificationCategory.Shares]: {
        section: NotificationSection.LiveStream,
        label: "A person shares with you",
    },
    [NotificationCategory.LocationSharing]: {
        section: NotificationSection.LocationSharing,
        label: "When any workspace member starts or stops sharing their location",
    },
}

const silenceOptions = [
    { label: "Don't silence", value: SilenceDuration.None },
    { label: "For 1 hour", value: SilenceDuration.OneHour },
    { label: "For 4 hours", value: SilenceDuration.FourHours },
    { label: "Until tomorrow", value: SilenceDuration.UntilTomorrow },
]

const channelColumns: { channel: NotificationChannel; label: string; bulkLabel: string }[] = [
    { channel: NotificationChannel.NovaWebApp, label: "App", bulkLabel: "Deselect All" },
    // { channel: NotificationChannel.Email, label: "Email", bulkLabel: "Select All" },
]

const sectionOrder = [
    NotificationSection.Notifications,
    NotificationSection.Processing,
    NotificationSection.LiveStream,
]

const entryKey = (category: string, channel: string) => `${category}:${channel}`

const formatSilenceUntil = (silenceUntil: string) =>
    new Date(silenceUntil).toLocaleString(undefined, {
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
    })

export const NotificationSettingsForm = ({
    value,
    onChange,
    currentSilenceUntil,
}: {
    value: NotificationSettingsDraft
    onChange: (next: NotificationSettingsDraft) => void
    currentSilenceUntil?: string | null
}) => {
    const enabledByKey = useMemo(() => {
        const map = new Map<string, boolean>()
        for (const entry of value.entries)
            map.set(entryKey(entry.category, entry.channel), entry.enabled)
        return map
    }, [value.entries])

    const now = new Date()
    const selectedSilence =
        value.silence ?? silenceUntilToDuration(currentSilenceUntil, now, now.getTimezoneOffset())

    const setEntry = (category: string, channel: NotificationChannel, enabled: boolean) => {
        const next: NotificationPreferenceEntry[] = value.entries.map((entry) =>
            entry.category === category && entry.channel === channel ? { ...entry, enabled } : entry
        )
        onChange({ ...value, entries: next })
    }

    const setColumn = (channel: NotificationChannel, enabled: boolean) => {
        const next: NotificationPreferenceEntry[] = value.entries.map((entry) =>
            entry.channel === channel ? { ...entry, enabled } : entry
        )
        onChange({ ...value, entries: next })
    }

    const columnState = (channel: NotificationChannel): TriCheckboxState => {
        const inColumn = value.entries.filter((entry) => entry.channel === channel)
        if (inColumn.length === 0) return "unchecked"
        const enabledCount = inColumn.filter((entry) => entry.enabled).length
        if (enabledCount === 0) return "unchecked"
        if (enabledCount === inColumn.length) return "checked"
        return "indeterminate"
    }

    const sections = useMemo(
        () =>
            sectionOrder.map((section) => ({
                section,
                rows: notificationCategoryMeta.filter(
                    (meta) => categoryDisplay[meta.category].section === section
                ),
            })),
        []
    )

    return (
        <div className="flex flex-col gap-6 text-white">
            <div className="flex flex-col gap-2">
                <small className="xs text-white">Silencing</small>
                {currentSilenceUntil && isSilenceActive(currentSilenceUntil, new Date()) && (
                    <small className="text-gray-300">
                        Currently silenced until {formatSilenceUntil(currentSilenceUntil)}
                    </small>
                )}
                <div className="flex items-center gap-2">
                    <h5 className="text-gray-100">Silence notifications until</h5>
                    <Select
                        options={silenceOptions}
                        value={selectedSilence}
                        onChange={(next) => onChange({ ...value, silence: next })}
                    />
                </div>
            </div>

            <div className="h-px w-full bg-gray-700" />

            <div className="flex flex-col">
                <div className="flex items-center gap-4 pb-1">
                    <div className="flex-1" />
                    {channelColumns.map((column) => (
                        <div
                            key={column.channel}
                            className="w-[100px] flex flex-col items-center gap-1"
                        >
                            <h5 className="text-white">{column.label}</h5>
                            <button
                                type="button"
                                className="text-gray-100 hover:text-white transition-colors"
                                onClick={() =>
                                    setColumn(
                                        column.channel,
                                        columnState(column.channel) !== "checked"
                                    )
                                }
                            >
                                <small>{column.bulkLabel}</small>
                            </button>
                        </div>
                    ))}
                </div>

                {sections.map(({ section, rows }) => (
                    <div key={section} className="flex flex-col border-t border-white-200 py-3">
                        <small className="xs text-white pb-2">{section}</small>
                        {rows.map((meta) => (
                            <div key={meta.category} className="flex items-center gap-4 py-1">
                                <h5 className="flex-1 text-white">
                                    {categoryDisplay[meta.category].label}
                                </h5>
                                {channelColumns.map((column) => {
                                    const applies = meta.channels.includes(column.channel)
                                    return (
                                        <div
                                            key={column.channel}
                                            className="w-[100px] flex items-center justify-center"
                                        >
                                            {applies && (
                                                <TriCheckbox
                                                    state={
                                                        enabledByKey.get(
                                                            entryKey(meta.category, column.channel)
                                                        )
                                                            ? "checked"
                                                            : "unchecked"
                                                    }
                                                    onClick={() =>
                                                        setEntry(
                                                            meta.category,
                                                            column.channel,
                                                            !enabledByKey.get(
                                                                entryKey(
                                                                    meta.category,
                                                                    column.channel
                                                                )
                                                            )
                                                        )
                                                    }
                                                />
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    )
}
